import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, Users, Briefcase, Calendar, Clock, AlertCircle, CheckCircle, Target } from 'lucide-react';
import employeeApi from '../../lib/employeeApi';
import EmployeeLayout from '../../components/EmployeeLayout';
import { getEmployeeUser } from '../../lib/employeeAuth';
import { exportToCSV } from '../../lib/csvExport';
import { formatDateToLocal } from '../../lib/dateUtils';
import { SearchableSelect } from '../../components/ui/searchable-select';

export default function ReportsPage() {
  const employee = getEmployeeUser();
  const [analytics, setAnalytics] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ month: formatDateToLocal(new Date()).slice(0, 7), client: '', department: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Send the current filters to every endpoint that supports them so
      // KPI cards + workload breakdowns react to the dropdowns.
      const params = {
        client_id: filters.client || undefined,
        department: filters.department || undefined,
        month: filters.month || undefined,
      };
      const [analyticsRes, tasksRes, empRes, clientsRes] = await Promise.all([
        employeeApi.get('/api/employee/tasks/analytics', { params }),
        employeeApi.get('/api/employee/tasks/enhanced'),
        employeeApi.get('/api/employees'),
        employeeApi.get('/api/clients')
      ]);
      setAnalytics(analyticsRes.data);
      // Client-side filtering for the workload tables so they mirror the
      // KPI cards (the tasks/enhanced endpoint doesn't accept these params
      // yet — this is faithful to what the user asked for).
      let list = tasksRes.data || [];
      if (filters.client) list = list.filter(t => t.client_id === filters.client);
      if (filters.department) list = list.filter(t => t.department === filters.department);
      if (filters.month) {
        const [y, m] = filters.month.split('-').map(Number);
        list = list.filter(t => {
          const ref = t.updated_at || t.created_at;
          if (!ref) return false;
          const d = new Date(ref);
          return d.getUTCFullYear() === y && (d.getUTCMonth() + 1) === m;
        });
      }
      setTasks(list);
      setEmployees(empRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const getClientTasks = (clientId) => tasks.filter(t => t.client_id === clientId);
  const getEmployeeTasks = (empId) => tasks.filter(t => t.primary_assignee_id === empId || t.created_by_employee_id === empId);

  if (loading) return <EmployeeLayout><div className="p-8 text-center"><div className="text-neutral-600">Loading...</div></div></EmployeeLayout>;

  return (
    <EmployeeLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Reports & Analytics</h1>
            <p className="text-charcoal-600">
              {/* All employees see department-scoped insights (Boss privileges eliminated) */}
              {`Insights for ${employee?.employment?.departments?.join(', ') || 'your'} Department(s)`}
            </p>
          </div>
          <div className="flex gap-4">
            <button onClick={() => exportToCSV(tasks, 'tasks_report')} className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium">Export CSV</button>
            <input type="month" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className="px-4 py-2 border border-charcoal-200 rounded-xl" />
            <SearchableSelect value={filters.client} onChange={(v) => setFilters({ ...filters, client: v })} placeholder="All Clients" triggerClassName="min-w-[180px] rounded-xl border-charcoal-200" options={[{ value: '', label: 'All Clients' }, ...clients.map(c => ({ value: c._id, label: c.name }))]} data-testid="reports-client-filter" />
            <SearchableSelect value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })} placeholder="All Departments" triggerClassName="min-w-[180px] rounded-xl border-charcoal-200" options={[{ value: '', label: 'All Departments' }, ...Object.keys(analytics?.by_department || {}).map(d => ({ value: d, label: d }))]} data-testid="reports-department-filter" />
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200  p-6 hover:shadow-lg transition-shadow">
            <Briefcase className="w-10 h-10 text-blue-600 mb-3" />
            <p className="text-sm text-blue-700 font-medium mb-1">Active Tasks</p>
            <p className="text-4xl font-bold text-blue-600">{analytics?.total_active_tasks || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200  p-6 hover:shadow-lg transition-shadow">
            <CheckCircle className="w-10 h-10 text-green-600 mb-3" />
            <p className="text-sm text-green-700 font-medium mb-1">Completed Today</p>
            <p className="text-4xl font-bold text-green-600">{analytics?.completed_today || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200  p-6 hover:shadow-lg transition-shadow">
            <AlertCircle className="w-10 h-10 text-red-600 mb-3" />
            <p className="text-sm text-red-700 font-medium mb-1">Overdue</p>
            <p className="text-4xl font-bold text-red-600">{analytics?.overdue_tasks || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200  p-6 hover:shadow-lg transition-shadow">
            <TrendingUp className="w-10 h-10 text-purple-600 mb-3" />
            <p className="text-sm text-purple-700 font-medium mb-1">In Revision</p>
            <p className="text-4xl font-bold text-purple-600">{analytics?.tasks_in_revision || 0}</p>
          </div>
        </div>

        {/* Time Tracking Metrics */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-charcoal-900 mb-4">Time Tracking Metrics</h2>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white  border border-charcoal-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-sm text-charcoal-600">Avg Start to Review</p>
                  <p className="text-xs text-neutral-600">My Tasks</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-charcoal-900">{analytics?.time_tracking?.my_tasks?.avg_start_to_review_hours || 0}<span className="text-lg text-charcoal-600">h</span></p>
            </div>
            <div className="bg-white  border border-charcoal-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-charcoal-600">Avg Revision to Complete</p>
                  <p className="text-xs text-neutral-600">My Tasks</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-charcoal-900">{analytics?.time_tracking?.my_tasks?.avg_revision_to_complete_hours || 0}<span className="text-lg text-charcoal-600">h</span></p>
            </div>
            <div className="bg-white  border border-charcoal-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-charcoal-600">Avg Total Turnaround</p>
                  <p className="text-xs text-neutral-600">My Tasks</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-charcoal-900">{analytics?.time_tracking?.my_tasks?.avg_start_to_complete_hours || 0}<span className="text-lg text-charcoal-600">h</span></p>
            </div>
          </div>
        </div>

        {/* Department Time Tracking */}
        <div className="mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200  p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-1">
                  {/* All employees see department-scoped performance (Boss privileges eliminated) */}
                  {employee?.employment?.departments?.join(', ') || 'Department'} Performance
                </h3>
                <p className="text-sm text-blue-700">
                  {analytics?.time_tracking?.department_tasks?.scope || 'Department'} - Average turnaround time
                </p>
              </div>
              <div className="text-right">
                <p className="text-5xl font-bold text-blue-600">{analytics?.time_tracking?.department_tasks?.avg_start_to_complete_hours || 0}<span className="text-2xl">h</span></p>
                <p className="text-sm text-blue-700 mt-1">Based on {analytics?.time_tracking?.department_tasks?.completed_count || 0} tasks</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="bg-white  border border-charcoal-200 p-6">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Tasks by Status</h3>
            <div className="space-y-3">
              {Object.entries(analytics?.by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${status === 'new' ? 'bg-blue-500' : status === 'in_progress' ? 'bg-purple-500' : status === 'review' ? 'bg-amber-500' : status === 'revision' ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                    <span className="text-charcoal-700 capitalize">{status.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-charcoal-100 rounded-full h-2">
                      <div className={`h-2 rounded-full ${status === 'new' ? 'bg-blue-500' : status === 'in_progress' ? 'bg-purple-500' : status === 'review' ? 'bg-amber-500' : status === 'revision' ? 'bg-orange-500' : 'bg-green-500'}`} style={{ width: `${(count / (analytics?.total_active_tasks || 1)) * 100}%` }}></div>
                    </div>
                    <span className="font-semibold text-charcoal-900 w-12 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white  border border-charcoal-200 p-6">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Tasks by Department</h3>
            <div className="space-y-3">
              {Object.entries(analytics?.by_department || {}).map(([dept, count]) => (
                <div key={dept} className="flex items-center justify-between">
                  <span className="text-charcoal-700 font-medium">{dept}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-charcoal-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-pink-500" style={{ width: `${(count / (analytics?.total_active_tasks || 1)) * 100}%` }}></div>
                    </div>
                    <span className="font-semibold text-charcoal-900 w-12 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Client & Employee Workload */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white  border border-charcoal-200 p-6">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Client Workload</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {clients.map(client => {
                const clientTasks = getClientTasks(client._id);
                return (
                  <div key={client._id} className="flex items-center justify-between p-3 bg-charcoal-50 rounded-xl hover:bg-charcoal-100 transition-colors">
                    <div>
                      <p className="text-charcoal-900 font-medium">{client.name}</p>
                      <p className="text-xs text-neutral-600">{client.industry}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-pink-600">{clientTasks.length}</p>
                      <p className="text-xs text-neutral-600">tasks</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white  border border-charcoal-200 p-6">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">
              {/* All employees see Team Workload (Boss privileges eliminated) */}
              Team Workload
            </h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {/* All employees see same-department colleagues only (Boss privileges eliminated) */}
              {employees.filter(e => 
                e.employment?.departments?.some(d => employee?.employment?.departments?.includes(d))
              ).map(emp => {
                const empTasks = getEmployeeTasks(emp._id);
                return (
                  <div key={emp._id} className="flex items-center justify-between p-3 bg-charcoal-50 rounded-xl hover:bg-charcoal-100 transition-colors">
                    <div>
                      <p className="text-charcoal-900 font-medium">{emp.personal?.full_name || emp.work_email}</p>
                      <p className="text-xs text-neutral-600">{emp.employment?.designation}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-pink-600">{empTasks.length}</p>
                      <p className="text-xs text-neutral-600">tasks</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </EmployeeLayout>
  );
}